import { useGrowl } from "PFApp/use_growl";
import SectionBox from "PFComponents/box/section_box";
import Modal from "PFComponents/modal/modal";
import { scoresShapePropType } from "PFCore/base/prop_types/common_prop_types";
import { Button } from "PFCore/components/button";
import { getProfileName } from "PFCore/helpers/profile";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import WarningIcon from "PFIcons/warning.svg";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { fillAndBookRole } from "../api";
import { getExceptionType } from "../helpers";
import css from "./modals.module.scss";
import { ExceptionModalContent } from "./parts/exception_modal_content";
import FillRoleContent from "./parts/fill_role_content";

const Step1 = ({ close, engagement, role, setStepIndex, exception, shortlistItem }) => {
  const exceptionType = getExceptionType(exception);
  const { t } = useTranslation("auditPlanner");

  const { profile } = shortlistItem;
  const shouldShowSoftOverbooking =
    !exception.availability &&
    !!shortlistItem?.scores?.availability_score &&
    shortlistItem?.scores?.availability_score !== 1;

  return (
    <FillRoleContent
      engagement={engagement}
      role={role}
      profile={profile}
      shouldShowSoftOverbooking={shouldShowSoftOverbooking}
    >
      <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
        <div>
          <WarningIcon
            className={css.icon}
            style={{ marginLeft: "unset", width: 90 }}
            width={60}
            height={60}
          />
        </div>

        <span style={{ marginLeft: 10 }}>
          <div className={css.bold}>
            {t("modals.potencialExceptionDetected", { exceptionType: exceptionType })}
          </div>
          <div> {t("modals.allActionsRecordedInLog")}</div>
        </span>
      </div>
      <div className={css.buttons}>
        <Button text={t("cancel")} kind="tertiary" onClick={close} />
        <Button text={t("continue")} onClick={() => setStepIndex(1)} />
      </div>
    </FillRoleContent>
  );
};

Step1.propTypes = {
  close: PropTypes.func.isRequired,
  setStepIndex: PropTypes.func.isRequired,
  exception: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
  engagement: PropTypes.object.isRequired,
  match: PropTypes.object,
  shortlistItem: PropTypes.shape({ profile: PropTypes.object.isRequired, scores: scoresShapePropType })
    .isRequired
};

const Step2 = ({ close, confirm, exception, role, shortlistItem }) => (
  <ExceptionModalContent
    role={role}
    shortlistItem={shortlistItem}
    exception={exception}
    confirm={confirm}
    close={close}
    isEditModal={false}
  />
);

Step2.propTypes = {
  close: PropTypes.func.isRequired,
  exception: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
  confirm: PropTypes.func.isRequired,
  shortlistItem: PropTypes.shape({ profile: PropTypes.object.isRequired }).isRequired
};

/* eslint-disable max-len */
const RequestApprovalModal = ({ close, exception, role, engagement, shortlistItem, onSuccess }) => {
  const growl = useGrowl();
  const { t } = useTranslation("auditPlanner");
  const { getFirstError } = useResponseErrors();

  const confirm = (note, approver) => {
    close();
    fillAndBookRole(shortlistItem.id, approver.id, note)
      .then(() => {
        if (onSuccess) {
          onSuccess();
        }
        growl({
          message: t("modals.requestSubmittedTo", {
            name: getProfileName(approver)
          }),
          kind: "success"
        });
      })
      .catch((response) => {
        const error = getFirstError(response);
        growl({
          message: error || t("somethingWentWrong"),
          kind: "error"
        });
      });
  };
  const [stepIndex, setStepIndex] = useState(0);
  const steps = [Step1, Step2];
  const CurrentStepComponent = steps[stepIndex];

  return (
    <Modal closeOnBackdropClick handleClose={close} style={{ maxWidth: 700, margin: "0 auto" }}>
      <SectionBox>
        <CurrentStepComponent
          setStepIndex={setStepIndex}
          close={close}
          confirm={confirm}
          exception={exception}
          role={role}
          engagement={engagement}
          shortlistItem={shortlistItem}
        />
      </SectionBox>
    </Modal>
  );
};

RequestApprovalModal.propTypes = {
  close: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  role: PropTypes.object.isRequired,
  exception: PropTypes.object.isRequired,
  engagement: PropTypes.object.isRequired,
  shortlistItem: PropTypes.shape({
    profile: PropTypes.object.isRequired,
    id: PropTypes.number.isRequired
  }).isRequired
};

export default RequestApprovalModal;
